import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectItem } from 'primeng/api';
import { VmeCommonModule } from '../../../vme-common.module';
import { VmePipesModule } from '../../../vme-pipes.module';
import { Message } from '../../../classes/Message';
import { OrderBookingHealthCheckService } from '../../../services/order-booking-health-check.service';
import { BookingAssignedToComponent } from '../order-booking-assigned-to/order-booking-assigned-to.component';
import { BookingIssueCardComponent } from '../booking-issue-card/booking-issue-card.component';
import { PillComponent } from '../../supplier-risk-management/components/pill/pill.component';
import {
  IAssigneeChange,
  IBookingIssue,
  IOrderBookingSummary,
} from '../../../interfaces/order-booking-health-check/order-booking-health-check';
import { OrderBookingIssueStatus } from '../../../enums/order-booking-health-check/order-booking-health-check.enums';
import { AMPriorityEnum } from '../../../enums/am-priority.enum';

@Component({
  selector: 'selected-booking',
  standalone: true,
  imports: [
    CommonModule,
    VmeCommonModule,
    VmePipesModule,
    BookingAssignedToComponent,
    BookingIssueCardComponent,
    PillComponent,
  ],
  templateUrl: './selected-booking.component.html',
})
export class SelectedBookingComponent {
  @Input() booking: IOrderBookingSummary;

  @Input() rechecking: boolean = false;

  @Output() assignedUserChange = new EventEmitter<IAssigneeChange>();
  @Output() issueChanged = new EventEmitter<void>();
  @Output() recheckRequested = new EventEmitter<void>();

  protected readonly OrderBookingIssueStatus = OrderBookingIssueStatus;
  protected readonly AMPriorityEnum = AMPriorityEnum;

  constructor(private orderBookingHealthCheckService: OrderBookingHealthCheckService) {}

  // Department and team, where they add anything. A team named the same as its department reads as
  // a repeat, so it only shows once.
  get scopeParts(): string[] {
    const parts = [this.booking?.customerDepartmentName, this.booking?.customerTeamName];

    return parts.filter((part, index) => !!part && parts.indexOf(part) === index);
  }

  get firstTask() {
    return this.booking?.tasks?.length ? this.booking.tasks[0] : null;
  }

  get lastTask() {
    return this.booking?.tasks?.length ? this.booking.tasks[this.booking.tasks.length - 1] : null;
  }

  // Open issues first, since those are the work; closed ones stay visible for context.
  get orderedIssues(): IBookingIssue[] {
    const openFirst = (issue: IBookingIssue) =>
      issue.status === OrderBookingIssueStatus.Open ? 0 : 1;

    return [...(this.booking?.issues ?? [])].sort(
      (a, b) => openFirst(a) - openFirst(b) || a.dateCreated.localeCompare(b.dateCreated),
    );
  }

  protected async handleAssignedUserChange(user: SelectItem) {
    const previousAssigneeId = this.booking.assigneeId;
    const previousAssigneeName = this.booking.assigneeName;

    // Applied optimistically and rolled back if the server refuses, so the dropdown never shows a
    // state the server did not accept.
    this.booking.assigneeId = user ? (user.value as string) : null;
    this.booking.assigneeName = user ? user.label : '';

    try {
      if (user) {
        await this.orderBookingHealthCheckService.assignUser(
          this.booking.orderId,
          user.value as string,
        );
      } else {
        await this.orderBookingHealthCheckService.unassignUser(this.booking.orderId);
      }

      this.assignedUserChange.emit({
        previousAssigneeId,
        assigneeId: this.booking.assigneeId,
      });
    } catch (error: any) {
      this.booking.assigneeId = previousAssigneeId;
      this.booking.assigneeName = previousAssigneeName;

      const reason = error?.error?.validationErrors?.[0]?.errorMessage;

      Message.Error(
        error?.status === 403
          ? 'You do not have permission to assign or unassign other users.'
          : reason ?? 'Could not change the assignment. Please try again.',
      );
    }
  }
}
